import { Controller } from '@hotwired/stimulus'

// Copies a native <input type="datetime-local"> wall-clock value into the
// approve form's hidden draft[scheduled_at] field as an absolute ISO-8601 string
// WITH the browser's UTC offset, so the server resolves the exact instant the
// member picked in their own timezone (the off-by-a-timezone risk a one-TZ
// system spec can't catch).
//
// A datetime-local value ("2026-06-17T14:30") carries NO offset. new Date(value)
// interprets it in the browser's local zone; toISOString() would force UTC and
// silently shift the wall clock. Instead we format the local components plus the
// real offset (e.g. "2026-06-17T14:30:00+03:00"), which the server parses with
// Time.zone.iso8601 (offset honored) → the correct UTC instant.
//
// Duration presets need NO conversion: their buttons submit a server-resolved
// token (draft[send_after]) straight through the allowlist, never a client
// datetime. And with no JS at all, the raw datetime-local value is rejected
// fail-closed server-side (ScheduledSendTime can't parse "T"-format wall clock),
// so a degraded picker can never mis-send — it just declines the schedule.
export default class extends Controller {
  static targets = ['input', 'scheduledAt']

  connect() {
    // Nothing to initialize — the controller only acts on the apply action.
  }

  disconnect() {
    // No listeners, timers, or instances held (Turbo-lifecycle convention).
  }

  // Reads the picked wall-clock time, stamps it with the browser's offset, and
  // writes the absolute ISO-8601 string into the hidden scheduled_at field that
  // the approve form posts. A blank/invalid pick clears the field (no schedule).
  apply() {
    if (!this.hasInputTarget || !this.hasScheduledAtTarget) return

    this.scheduledAtTarget.value = this.toIso8601WithOffset(this.inputTarget.value)
  }

  // "2026-06-17T14:30" (+ local offset) -> "2026-06-17T14:30:00+03:00".
  // Returns "" for a blank/unparsable value so the hidden field stays empty.
  toIso8601WithOffset(localValue) {
    if (!localValue) return ''

    const date = new Date(localValue)
    if (Number.isNaN(date.getTime())) return ''

    const pad = (n) => String(n).padStart(2, '0')
    const y = date.getFullYear()
    const mo = pad(date.getMonth() + 1)
    const d = pad(date.getDate())
    const h = pad(date.getHours())
    const mi = pad(date.getMinutes())
    const s = pad(date.getSeconds())

    // getTimezoneOffset() is minutes BEHIND UTC (e.g. -180 for UTC+3), so the
    // sign is inverted relative to the printed offset.
    const offsetMinutes = -date.getTimezoneOffset()
    const sign = offsetMinutes >= 0 ? '+' : '-'
    const abs = Math.abs(offsetMinutes)
    const offset = `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`

    return `${y}-${mo}-${d}T${h}:${mi}:${s}${offset}`
  }
}
